// The game huts (你画我猜; 谁是卧底 or 狼人杀, the second hut lets you choose; OtterBonk, games-otterbonk.js; 心灵捕手, games-mind.js). Walking into a hut shows a small card
// to start a game; once a game runs it takes the whole screen (Esc or 「离开」
// goes back to the map, the voice in the hut keeps going). The server runs the
// rounds; this only shows them. Every name and message goes in as text.
import { initWerewolf } from '/games-werewolf.js';
import { initMind } from '/games-mind.js';
import { initOtterBonk } from '/games-otterbonk.js';
import { icon, put, lead, hasIcon } from '/icons.js';
import { autoTour, tourButton } from '/tours.js';
import { t, tr, has, onLang } from '/i18n.js';
import { voiceNote } from '/game-voice.js';
import { recordsButton } from '/records.js';
const $ = (id) => document.getElementById(id);
const CSS_URL = new URL('./games.css', import.meta.url).pathname; // its URL: Vite names the built file by its content (vite.config.mjs)
if (!document.querySelector(`link[href="${CSS_URL}"]`)) {
  const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = CSS_URL; document.head.appendChild(l);
}
const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
const secsLeft = (t) => Math.max(0, Math.ceil((t - Date.now()) / 1000));
const BOARD_W = 1000, BOARD_H = 700;
const COLORS = ['#1b1410', '#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#a855f7', '#8b5a2b'];
const SIZES = [4, 8, 16], ERASER = 28;
/** Each full screen's coach marks (public/tours.js): shown the first time it opens, again from its 「玩法」. */
const TOUR = { gfull: 'pictionary', sfull: 'spy', wfull: 'werewolf', mfull: 'mind' };
/** Each full screen's game in 战绩 (public/records.js), for its 战绩 button (none: not recorded yet). */
const REC = { gfull: 'pictionary', sfull: 'spy', wfull: 'werewolf', mfull: 'mind' };

/** A title, rules or placeholder given to shell() / lobby(): its text, or a function giving it (drawn again on 设置 → 语言). */
const txt = (v) => (typeof v === 'function' ? v() : v);
/** The full-screen shell both games share: top bar, a stage on the left, scores + messages + input on the right. */
function shell(id, title, placeholder, ic) {
  const root = el('div', 'gfull'); root.id = id; root.style.display = 'none';
  const top = el('div', 'gtop');
  const ttl = el('b', 'gtitle'), drawTitle = () => put(ttl, ...(ic ? [ic, txt(title)] : lead(txt(title)))); drawTitle();
  const round = el('span', 'gpill'), who = el('span', 'gwho'), word = el('span', 'gword'), time = el('span', 'gtimer');
  const leave = tr(el('button', 'gleave'), 'games.leave', null, { title: 'games.leaveTip' });
  top.append(ttl, round, who, word, el('span', 'gspace'), time, ...(REC[id] ? [recordsButton(REC[id])] : []), tourButton(TOUR[id]), leave);
  const body = el('div', 'gbody'), stage = el('div', 'gstage'), side = el('div', 'gside');
  const scores = el('div', 'gscores'), log = el('div', 'glog'), form = el('form', 'ginput'), input = el('input'), sendBtn = tr(el('button'), 'games.send');
  input.placeholder = txt(placeholder); input.maxLength = 60; input.autocomplete = 'off';
  form.append(input, sendBtn); side.append(scores, log, form);
  const card = el('div', 'gcard'); card.style.display = 'none';
  body.append(stage, side); root.append(top, body);
  document.body.appendChild(root);
  onLang(() => { drawTitle(); if (typeof placeholder === 'function') input.placeholder = placeholder(); });
  for (const ev of ['keydown', 'keyup']) input.addEventListener(ev, (e) => { e.stopPropagation(); if (e.key === 'Escape') input.blur(); });
  return { root, round, who, word, time, leave, stage, scores, log, form, input, sendBtn, card };
}
/** A param of a line the office sent as a code (docs/i18n.md): a seat { n, name } as "3号 Ann", { name, gain } as
 *  "Ann +2", a 狼人杀 vote { from, to } as "1号 Ann → 2号 Bob", a list joined, nothing as ''. */
function param(v) {
  // (points and votes were joined with ，, names with 、: as the office's words were)
  if (Array.isArray(v)) return v.map(param).join(t(v.some((x) => x && typeof x === 'object' && ('gain' in x || 'from' in x)) ? 'games.sepLong' : 'games.sep'));
  if (v && typeof v === 'object') return 'gain' in v ? t('games.gain', v) : 'n' in v ? (v.n ? t('ww.seat', v) : v.name ?? '?') : 'from' in v ? t('ww.log.voteItem', { from: param(v.from), to: param(v.to), abstain: v.to ? 'no' : 'yes' }) : '';
  return v ?? '';
}
/** A line's words: `${prefix}.${m.code}` with its params if this page has that code, else the office's own words. */
function coded(prefix, m) {
  const key = prefix && m.code ? `${prefix}.${m.code}` : '';
  if (!key || !has(key)) return m.text;
  return t(key, Object.fromEntries(Object.entries(m.params ?? {}).map(([k, v]) => [k, param(v)])));
}
const logs = new Map(); // a log line with a code -> how to draw it again (设置 → 语言)
function addLog(box, m, prefix) {
  const d = el('div', m.good ? 'good' : m.name ? '' : 'sys');
  // m.icon: the office's pixel icon for a system line (its text has no emoji, for agents); an older office's line may
  // still start with an emoji: shown as the icon too. m.nameIcon: one in front of the name (狼人杀's wolf / ghost chat).
  // m.code (+ m.params): the line as a code (prefix: the hut's, e.g. ww.log), worded here; m.text for an older office.
  const draw = () => {
    const [ic, text] = m.icon ? [m.icon, coded(prefix, m)] : lead(coded(prefix, m));
    d.replaceChildren();
    if (m.name) { const b = put(el('b'), m.nameIcon, t('games.log.name', { name: m.name })); d.append(b, ...(ic ? [icon(ic), ' '] : []), text); } else put(d, ic, text);
  };
  draw();
  if (m.code) logs.set(d, draw);
  box.appendChild(d); while (box.childNodes.length > 200) { logs.delete(box.firstChild); box.firstChild.remove(); }
  box.scrollTop = 1e9;
}
onLang(() => { for (const [d, draw] of logs) if (d.isConnected) draw(); else logs.delete(d); });
function scoreList(box, rows, mark) {
  box.replaceChildren(el('div', 'ghead', t('games.scores')));
  rows.forEach((s, i) => {
    const r = el('div', 'grow' + (s.here === false ? ' gone' : ''));
    // mark(s): what follows the name: a string, or a list of strings and icon names
    const nm = el('span', 'gname', s.name);
    for (const x of [].concat(mark?.(s) ?? [])) if (x) nm.append(...(hasIcon(x) ? [' ', icon(x)] : [x]));
    r.append(el('span', 'grank', String(i + 1)), nm, el('span', 'gpts', String(s.score)));
    box.appendChild(r);
  });
}
function leaderboard(card, title, rows, me, buttons) {
  card.replaceChildren(put(el('div', 'gctitle'), ...lead(title, 'trophy')));
  const list = el('ol', 'gboardlist');
  rows.forEach((s, i) => {
    const li = el('li', (s.id === me ? 'mine ' : '') + (i < 3 ? 'top' + i : ''));
    li.append(i < 3 ? (() => { const m = el('span', 'gmedal'); m.append(icon('medal_' + (i + 1))); return m; })() : el('span', 'gmedal', String(i + 1)), el('span', 'gname', s.name), el('span', 'gpts', t('games.points', { n: s.score })));
    list.appendChild(li);
  });
  card.appendChild(list);
  const row = el('div', 'gcbtns'); for (const b of buttons) row.appendChild(b); card.appendChild(row);
}
const btn = (text, cls, fn) => { const b = el('button', cls, text); b.onclick = fn; return b; };

/** The small card shown in a hut when no game is on screen. */
function lobby(id, title, rules, ic, rec) {
  const d = el('div', 'globby'); d.id = id; d.style.display = 'none';
  const head = el('div', 'glhead'), ttl = el('b'), drawTitle = () => put(ttl, ...(ic ? [ic, txt(title)] : lead(txt(title)))); drawTitle(); head.append(ttl); const count = el('span', 'glcount'); head.append(count);
  const status = el('div', 'glstatus'), go = el('button', 'gprimary'), see = tr(el('button', 'glsee'), 'games.seeBoard');
  const row = el('div', 'glbtns'); row.append(go, see);
  const rulesP = el('p', 'glrules', txt(rules));
  if (rec) row.append(recordsButton(rec, '')); // 战绩 (public/records.js)
  d.append(head, status, rulesP, row);
  document.body.appendChild(d);
  onLang(() => { drawTitle(); if (typeof rules === 'function') rulesP.textContent = rules(); });
  return { d, count, status, go, see };
}

export function initGames({ send, me, zoneAt, peers, stopWalking = () => {}, walkToZone = () => false, guest = () => false, micOn = () => true }) {
  /** Who I am in the games: my Raft login (gid), the same across reloads and reconnects. */
  const myId = () => me()?.gid ?? me()?.id;
  const inZone = (zid) => { const m = me(); return !!m && zoneAt(m.x, m.y)?.id === zid; };
  const hutCount = (zid) => [me(), ...peers().values()].filter((p) => p && zoneAt(p.x, p.y)?.id === zid).length;
  const fulls = [];
  // Esc leaves full screen; while it is up, keys don't walk the otter out of the hut (M still mutes).
  addEventListener('keydown', (e) => {
    const open = fulls.find((f) => f.isOpen());
    if (!open || ((e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') && !(e.key === 'Escape' && e.target.type === 'range')) || e.target.closest?.('.dd-list')) return; // an open dropdown (public/dropdown.js) has its own keys
    if (e.key === 'Escape') { open.close(); e.stopPropagation(); return; }
    // The drawer's undo / redo: Ctrl/Cmd+Z, Ctrl/Cmd+Shift+Z or Ctrl/Cmd+Y.
    if ((e.ctrlKey || e.metaKey) && !e.altKey && open.undo) {
      const k = e.key.toLowerCase();
      if (k === 'z' || k === 'y') { e.preventDefault(); e.stopPropagation(); open.undo(k === 'y' || e.shiftKey); return; }
    }
    // Enter jumps to the game's input, unless a button or link has focus: then Enter presses it.
    if (e.key === 'Enter') { if (!e.repeat && !e.target.closest?.('button, a, select, textarea, [contenteditable]')) { open.focus(); e.preventDefault(); } e.stopPropagation(); return; }
    if (e.key.toLowerCase() === 'm' && !e.metaKey && !e.ctrlKey) return;
    e.stopPropagation();
  }, true);

  // ---------------- 你画我猜
  const G = { phase: 'idle', drawer: null, endsAt: 0, round: 0, rounds: 0, word: '', clue: null, undo: 0, redo: 0, color: COLORS[0], size: SIZES[0], eraser: false, scores: [], players: [], open: false, dismissed: false };
  const g = shell('gfull', () => t('games.pict.title'), () => t('games.pict.guessPh'), 'palette');
  const gl = lobby('globby', () => t('games.pict.title'), () => t('games.pict.rules'), 'palette', 'pictionary');
  const wrap = el('div', 'gboardwrap'), gb = el('canvas', 'gcanvas'); gb.width = BOARD_W; gb.height = BOARD_H; gb.id = 'gboard';
  wrap.append(gb, g.card);
  const tools = el('div', 'gtools');
  const sws = COLORS.map((c) => { const s = el('button', 'sw'); s.style.background = c; tr(s, null, null, { title: 'games.pict.color' }); s.onclick = () => { G.color = c; G.eraser = false; paintTools(); }; return s; });
  const sizes = SIZES.map((w) => { const s = el('button', 'sz'); const dot = el('i'); dot.style.width = dot.style.height = Math.max(4, w * 0.9) + 'px'; s.append(dot); tr(s, null, null, { title: 'games.pict.size' }); s.onclick = () => { G.size = w; G.eraser = false; paintTools(); }; return s; });
  const eraser = btn(t('games.pict.eraser'), 'gtool', () => { G.eraser = true; paintTools(); });
  const clear = btn(t('games.pict.clear'), 'gtool', () => { gClear(); send({ t: 'g_clear' }); });
  // Undo / redo: the office changes the board and sends everyone the board as it is now (g_board).
  const undoRedo = (redo) => { if (!iDraw()) return; endStroke(); send({ t: redo ? 'g_redo' : 'g_undo' }); };
  const isMac = /Mac|iPhone|iPad/.test(navigator.platform);
  const undoBtn = btn('', 'gtool ghist', () => undoRedo(false)), redoBtn = btn('', 'gtool ghist', () => undoRedo(true));
  undoBtn.append(icon('undo')); redoBtn.append(icon('redo'));
  tr(undoBtn, null, { keys: isMac ? '⌘Z' : 'Ctrl+Z' }, { title: 'games.pict.undoTip', 'aria-label': 'games.pict.undo' });
  tr(redoBtn, null, { keys: isMac ? '⇧⌘Z' : 'Ctrl+Shift+Z / Ctrl+Y' }, { title: 'games.pict.redoTip', 'aria-label': 'games.pict.redo' });
  tr(eraser, 'games.pict.eraser'); tr(clear, 'games.pict.clear');
  tools.append(...sws, el('span', 'gsep'), ...sizes, el('span', 'gsep'), eraser, clear, el('span', 'gsep'), undoBtn, redoBtn);
  const drawHint = el('div', 'gdrawhint');
  g.stage.append(wrap, tools, drawHint);
  function paintTools() {
    sws.forEach((s, i) => s.classList.toggle('on', !G.eraser && G.color === COLORS[i]));
    sizes.forEach((s, i) => s.classList.toggle('on', !G.eraser && G.size === SIZES[i]));
    eraser.classList.toggle('on', G.eraser);
    undoBtn.disabled = !G.undo; redoBtn.disabled = !G.redo;
  }
  paintTools();
  const gx = gb.getContext('2d'); gx.lineCap = 'round'; gx.lineJoin = 'round';
  function stroke(st) {
    gx.strokeStyle = st.c; gx.lineWidth = st.w; gx.beginPath();
    for (let i = 0; i < st.p.length; i += 2) (i ? gx.lineTo : gx.moveTo).call(gx, st.p[i], st.p[i + 1]);
    if (st.p.length === 2) gx.lineTo(st.p[0] + 0.1, st.p[1]);
    gx.stroke();
  }
  function gClear() { gx.fillStyle = '#fff'; gx.fillRect(0, 0, gb.width, gb.height); }
  gClear();
  const iDraw = () => G.phase === 'drawing' && G.drawer === myId();
  let drawing = null, strokeId = 0;
  const gpos = (e) => { const r = gb.getBoundingClientRect(); return [Math.round(((e.clientX - r.left) / r.width) * BOARD_W), Math.round(((e.clientY - r.top) / r.height) * BOARD_H)]; };
  gb.addEventListener('pointerdown', (e) => {
    if (!iDraw()) return;
    gb.setPointerCapture(e.pointerId);
    // k: this pen stroke's id; its chunks carry the same one, so the office undoes them together
    drawing = { c: G.eraser ? '#ffffff' : G.color, w: G.eraser ? ERASER : G.size, p: gpos(e), k: ++strokeId }; stroke(drawing);
  });
  gb.addEventListener('pointermove', (e) => {
    if (!drawing) return;
    const [x, y] = gpos(e), p = drawing.p; p.push(x, y);
    stroke({ c: drawing.c, w: drawing.w, p: p.slice(-4) });
    if (p.length >= 80) { send({ t: 'g_draw', ...drawing }); drawing = { c: drawing.c, w: drawing.w, p: [x, y], k: drawing.k }; }
  });
  const endStroke = () => { if (drawing && drawing.p.length) send({ t: 'g_draw', ...drawing }); drawing = null; };
  gb.addEventListener('pointerup', endStroke); gb.addEventListener('pointercancel', endStroke);
  g.form.onsubmit = (e) => { e.preventDefault(); const v = g.input.value.trim(); if (v) send({ t: 'g_guess', text: v }); g.input.value = ''; };

  /** The board as the office has it now (after an undo or redo, or walking in): everything drawn again. */
  function gBoard(strokes) {
    gClear();
    for (const st of strokes) stroke(st);
    if (drawing) stroke(drawing);
  }
  const gFull = {
    isOpen: () => G.open,
    undo: (redo) => undoRedo(redo),
    close: () => { G.open = false; G.dismissed = true; render(); },
    focus: () => { if (!g.input.disabled) g.input.focus(); },
  };
  fulls.push(gFull);
  g.leave.onclick = gFull.close;
  const again = () => btn(t('games.again'), 'gprimary', () => send({ t: 'g_start' }));

  function gRender() {
    const mine = myId(), iAmDrawer = G.drawer === mine && G.phase === 'drawing', playing = G.players.includes(mine);
    g.round.textContent = G.rounds ? t('games.pict.round', { round: G.round, rounds: G.rounds }) : '';
    put(g.who, G.phase === 'drawing' ? 'pencil' : null, G.phase === 'drawing' ? (iAmDrawer ? t('games.pict.yourTurn') : t('games.pict.drawing', { name: G.drawerName })) : G.phase === 'reveal' ? t('games.pict.roundOver') : G.phase === 'over' ? t('games.gameOver') : '');
    // Guessers: a blank per character (the one the second hint gives filled in), the length, then the category.
    const c = G.clue, chars = c?.chars ?? Array(G.hint).fill(null);
    const given = c?.n ? t('games.pict.given', { hint: c.category ? (c.n >= 2 ? 'both' : 'cat') : c.n >= 2 ? 'char' : 'none', category: c.category ?? '' }) : '';
    if (G.phase === 'drawing' && !iAmDrawer) {
      const blanks = el('span', 'gblanks');
      for (const ch of chars) blanks.append(ch ? el('b', 'gshown', ch) : document.createTextNode(t('games.pict.blank')));
      g.word.replaceChildren(blanks, el('span', 'gcount', t('games.pict.chars', { n: chars.length })));
      if (c?.category) g.word.append(el('span', 'gcat', t('games.pict.category', { category: c.category })));
    } else g.word.textContent = G.phase === 'drawing' ? t('games.pict.drawThis', { word: G.word, given }) : G.reveal ? t('games.pict.answer', { word: G.reveal }) : '';
    g.word.classList.toggle('mine', iAmDrawer); g.word.classList.toggle('hint', G.phase === 'drawing' && !iAmDrawer);
    tools.style.display = iAmDrawer ? '' : 'none';
    gb.classList.toggle('can', iAmDrawer);
    drawHint.textContent = iAmDrawer ? t('games.pict.drawHint', { keys: isMac ? '⌘Z' : 'Ctrl+Z' }) : G.phase === 'drawing' && !playing ? t('games.pict.watching') : '';
    g.input.disabled = iAmDrawer;
    g.input.placeholder = t(iAmDrawer ? 'games.pict.ph.drawing' : G.guessed?.includes(mine) ? 'games.pict.ph.guessed' : G.phase === 'drawing' ? 'games.pict.guessPh' : 'games.pict.ph.chat');
    // away: dropped off, their place held for a while (the office's g_state away; none from an older office)
    scoreList(g.scores, G.scores, (s) => [s.id === G.drawer && 'pencil', G.guessed?.includes(s.id) && 'check', s.here === false && t('games.scoreLeft'), G.away?.includes(s.id) && t('pict.away')]);
    if (G.phase === 'reveal') {
      g.card.replaceChildren(el('div', 'gctitle', t(G.why === 'all' ? 'games.pict.allGuessed' : 'games.pict.timeUp')), el('div', 'gcanswer', t('games.pict.answerWas', { word: G.reveal })));
      const list = el('div', 'ggains');
      if (!G.gains.length) list.append(el('div', 'gsys', t('games.pict.noScore')));
      for (const x of G.gains) { const r = el('div', 'grow'); r.append(el('span', 'gname', x.name), el('span', 'gplus', '+' + x.gain)); list.append(r); }
      g.card.append(list, el('div', 'gnext', t(G.round < G.rounds ? 'games.pict.nextRound' : 'games.boardSoon')));
      g.card.style.display = '';
    } else if (G.phase === 'over') {
      leaderboard(g.card, t('games.leaderboard'), G.scores, mine, [again(), btn(t('games.leave'), '', gFull.close)]);
      g.card.style.display = '';
    } else g.card.style.display = 'none';
  }

  // ---------------- 谁是卧底
  const S = { phase: 'idle', endsAt: 0, word: '', speaker: null, order: [], voted: [], scores: [], open: false, dismissed: false, myVote: null };
  const s = shell('sfull', () => t('games.spy.title'), () => t('games.spy.ph.start'), 'spy');
  const sl = lobby('slobby', () => t('games.hut.title'), '', 'spy', 'spy');
  // This hut has two games: pick one to start.
  // title, need, rules: catalog keys
  const pick = (ic, title, need, rules, start) => {
    const d = el('div', 'glpick'), head = el('div', 'glphead'), go = btn(t('games.start'), 'gprimary', () => send({ t: start }));
    const name = el('b'), drawName = () => put(name, ic, t(title)); drawName(); onLang(drawName);
    head.append(name, tr(el('span', 'glneed'), need), go);
    d.append(head, tr(el('p', 'glrules'), rules));
    return { d, go };
  };
  const pickSpy = pick('spy', 'games.spy.title', 'games.hut.spy.need', 'games.hut.spy.rules', 's_start');
  const pickWw = pick('wolf', 'games.hut.ww.title', 'games.hut.ww.need', 'games.hut.ww.rules', 'w_start');
  const picks = el('div', 'glpicks'); picks.append(pickSpy.d, pickWw.d);
  sl.d.querySelector('.glrules').replaceWith(picks);
  const myWord = el('div', 'smyword'), phaseLine = el('div', 'sphase'), sVoice = el('div', 'gvoice'), seats = el('div', 'sseats');
  const sStage = el('div', 'sstage'); sStage.append(myWord, phaseLine, sVoice, seats, s.card);
  /** Where my voice goes (game-voice.js), by the office's rules (spy.ts mayHear): while it's described only the speaker
   *  is heard by the table; in the vote the living talk; the out and onlookers among themselves. */
  const spyVoice = () => {
    if (S.phase !== 'describe' && S.phase !== 'vote') return null;
    const seat = S.order.find((o) => o.id === myId());
    return !seat?.alive ? 'ghost' : S.phase === 'vote' || S.speaker === seat.id ? 'all' : 'turn';
  };
  s.stage.append(sStage);
  const done = tr(el('button', 'gprimary'), 'games.spy.done'); s.form.append(done); s.sendBtn.remove();
  s.form.onsubmit = (e) => { e.preventDefault(); if (S.phase === 'describe' && S.speaker === myId()) { send({ t: 's_done', text: s.input.value }); s.input.value = ''; } };
  const sFull = {
    isOpen: () => S.open,
    close: () => { S.open = false; S.dismissed = true; render(); },
    focus: () => { if (!s.input.disabled) s.input.focus(); },
  };
  fulls.push(sFull);
  s.leave.onclick = sFull.close;

  function sRender() {
    const mine = myId(), seat = S.order.find((o) => o.id === mine), alive = !!seat?.alive;
    const speakerName = S.order.find((o) => o.id === S.speaker)?.name ?? '?';
    s.round.textContent = S.game ? t('games.spy.round', { game: S.game, games: S.games, round: S.round }) : '';
    s.who.textContent = '';
    s.word.textContent = '';
    myWord.replaceChildren();
    if (S.phase !== 'idle' && S.phase !== 'final' && seat) myWord.append(el('span', 'slabel', t('games.spy.yourWord')), el('b', '', S.word || '…'));
    else if (S.phase !== 'idle' && S.phase !== 'final') myWord.append(el('span', 'slabel', t('games.watching')), el('b', 'small', t('games.spy.joinNext')));
    put(phaseLine, ...(S.phase === 'describe' ? ['mic', S.speaker === mine ? t('games.spy.yourTurn') : t('games.spy.describing', { name: speakerName })]
      : S.phase === 'vote' ? (alive ? (S.myVote ? [null, t('games.spy.voted')] : ['vote', t('games.spy.vote')]) : [null, t('games.spy.voteOut')])
      : S.phase === 'reveal' ? lead(t('games.spy.revealNext', { note: S.note ? coded('games.spy.log', S.note) : t('games.spy.revealing') })) : S.phase === 'over' ? (S.winner === 'civilians' ? ['party', t('games.spy.civWin')] : S.winner === 'spies' ? ['spy', t('games.spy.spyWin')] : [null, t('games.spy.endedEarly')]) : [null, '']));
    voiceNote(sVoice, spyVoice(), micOn());
    // Seats are rebuilt on every update: keep keyboard focus on the same seat so Tab/Enter/Space voting works.
    const focused = seats.contains(document.activeElement) ? document.activeElement.dataset.seat : null;
    seats.replaceChildren();
    for (const o of S.order) {
      const b = el('button', 'seat' + (o.id === S.speaker ? ' speaking' : '') + (o.alive ? '' : ' out') + (o.id === S.myVote ? ' myvote' : '') + (o.id === mine ? ' me' : ''));
      const tag = !o.alive ? t('games.spy.tag.out') : S.away?.includes(o.id) ? t('spy.away') : o.id === S.speaker ? t('games.spy.tag.speaking') : S.phase === 'vote' && S.voted.includes(o.id) ? t('games.spy.tag.voted') : '';
      b.append(el('span', 'sav', [...o.name][0] ?? '?'), el('span', 'sname', o.id === mine ? t('games.spy.nameMe', { name: o.name }) : o.name), (() => { const tg = el('span', 'stag', tag); if (S.reveal?.spies.includes(o.id)) tg.append(' ', icon('spy'), ' ', t('games.spy.spy')); return tg; })());
      const canVote = S.phase === 'vote' && alive && o.alive && o.id !== mine;
      b.disabled = !canVote; b.classList.toggle('votable', canVote);
      b.onclick = () => { if (canVote) { S.myVote = o.id; send({ t: 's_vote', id: o.id }); sRender(); } };
      b.dataset.seat = o.id; seats.appendChild(b);
      if (o.id === focused && !b.disabled) b.focus();
    }
    const myTurn = S.phase === 'describe' && S.speaker === mine;
    s.input.disabled = !myTurn; done.style.display = myTurn ? '' : 'none';
    s.input.placeholder = myTurn ? t('games.spy.ph.turn') : seat ? t('games.spy.ph.wait') : t('games.watching');
    scoreList(s.scores, S.scores);
    if (S.phase === 'over' && S.reveal) {
      s.card.replaceChildren(el('div', 'gctitle', phaseLine.textContent));
      const words = el('div', 'swords');
      words.append(el('div', '', t('games.spy.civWord', { word: S.reveal.civilian })), el('div', '', t('games.spy.spyWord', { word: S.reveal.spy })),
        el('div', '', t('games.spy.spies', { names: S.reveal.spies.map((id) => S.order.find((o) => o.id === id)?.name ?? '?').join(t('games.sep')) })));
      const list = el('div', 'ggains');
      for (const x of S.gains) { const r = el('div', 'grow'); r.append(el('span', 'gname', x.name), el('span', 'gplus', '+' + x.gain)); list.append(r); }
      s.card.append(words, list, el('div', 'gnext', t(S.game < S.games ? 'games.spy.nextGame' : 'games.boardSoon')));
      s.card.style.display = '';
    } else if (S.phase === 'final') {
      leaderboard(s.card, t('games.leaderboard'), S.scores, mine, [btn(t('games.again'), 'gprimary', () => send({ t: 's_start' })), btn(t('games.leave'), '', sFull.close)]);
      if (S.reveal) s.card.insertBefore(el('div', 'gsys', t('games.spy.lastWords', { civ: S.reveal.civilian, spy: S.reveal.spy })), s.card.children[1]);
      s.card.style.display = '';
    } else s.card.style.display = 'none';
  }

  // ---------------- 狼人杀 (public/games-werewolf.js), in the same hut
  const ww = initWerewolf({ send, myId, shell, el, btn, addLog, coded, scoreList, leaderboard, fulls, render: () => render(), micOn });
  const W = ww.W;
  // ---------------- 心灵捕手 (public/games-mind.js), its own hut
  const mind = initMind({ send, myId, shell, lobby, el, btn, addLog, scoreList, leaderboard, fulls, inZone, hutCount, micOn });

  // ---------------- OtterBonk (public/games-otterbonk.js), its own hut; the run mounts its own full screen
  // someone's otter as the office draws it (their look, else the one made from their seed), by game id
  const lookOf = (gid) => { const p = [me(), ...peers().values()].find((q) => q && (q.gid ?? q.id) === gid); return p ? { look: p.look ?? null, seed: p.seed || p.id } : null; };
  const ob = initOtterBonk({ send, myId, myName: () => me()?.name ?? '', lookOf, lobby, el, inZone, hutCount, stopWalking, fulls, walkToZone, guest });

  // ---------------- showing and hiding
  function render() {
    // The spy hut shows one game full screen at a time: 狼人杀 when it is open, else 谁是卧底.
    const inSpy = inZone('spy');
    if (!inSpy) { W.open = false; W.dismissed = false; }
    if (W.open) S.open = false;
    ww.root.style.display = inSpy && W.open ? '' : 'none';
    if (inSpy && W.open && ww.running() && W.seats.some((x) => x.id === myId())) autoTour('werewolf'); // players, not spectators
    for (const [st, full, lob, zid, min, startMsg] of [[G, g, gl, 'game', 2, 'g_start'], [S, s, sl, 'spy', 3, 's_start']]) {
      const here = inZone(zid), on = st.phase !== 'idle';
      if (!here) { st.open = false; st.dismissed = false; }
      full.root.style.display = here && st.open ? '' : 'none';
      if (here && st.open && st.phase !== 'over' && st.phase !== 'final' && (st === G ? G.players : S.order.map((o) => o.id)).includes(myId())) autoTour(TOUR[full.root.id]); // playing (not watching, not the leaderboard)
      lob.d.style.display = here && !st.open && !(st === S && W.open) ? '' : 'none';
      if (!here || st.open || (st === S && W.open)) continue;
      lob.see.style.display = on ? '' : 'none';
      lob.see.onclick = () => { st.open = true; st.dismissed = false; render(); };
      const n = hutCount(zid);
      lob.count.textContent = t('games.inHut', { n });
      const ended = G === st ? G.phase === 'over' : S.phase === 'final';
      if (st === S) {
        // Two games here: a running one gets 「回到游戏」, otherwise choose which to start.
        const wwOn = ww.running(), spyOn = on && !ended;
        picks.style.display = wwOn || spyOn ? 'none' : '';
        lob.go.style.display = wwOn || spyOn ? '' : 'none';
        lob.see.style.display = !wwOn && !spyOn && (W.phase === 'over' || S.phase === 'final') ? '' : 'none';
        lob.see.onclick = () => { if (W.phase === 'over') ww.open(); else { S.open = true; S.dismissed = false; render(); } };
        if (wwOn) { lob.status.textContent = t('games.hut.ww.running', { phase: W.day ? (W.phase === 'night' ? 'night' : 'day') : 'deal', day: W.day }); lob.go.textContent = t('games.back'); lob.go.onclick = ww.open; }
        else if (spyOn) { lob.status.textContent = t('games.hut.spy.running', { game: S.game, games: S.games }); lob.go.textContent = t('games.back'); lob.go.onclick = () => { S.open = true; S.dismissed = false; render(); }; }
        else {
          lob.status.textContent = t('games.hut.pick');
          pickSpy.go.disabled = n < 3; pickSpy.go.title = n < 3 ? t('games.hut.needMore', { min: 3, more: 3 - n }) : '';
          pickWw.go.disabled = n < 6 || n > 12; pickWw.go.title = n < 6 ? t('games.hut.needMore', { min: 6, more: 6 - n }) : n > 12 ? t('games.hut.max', { max: 12 }) : '';
          pickSpy.go.textContent = t(S.phase === 'final' ? 'games.again' : 'games.start');
          pickWw.go.textContent = t(W.phase === 'over' ? 'games.again' : 'games.start');
        }
        continue;
      }
      if (on && !ended) {
        lob.status.textContent = st === G ? t('games.running.round', { round: G.round, rounds: G.rounds }) : t('games.running.game', { game: S.game, games: S.games });
        lob.go.textContent = t('games.back'); lob.go.onclick = lob.see.onclick; lob.see.style.display = 'none';
      } else {
        lob.status.textContent = n >= min ? t('games.ready') : t('games.needMoreToStart', { min, more: min - n });
        lob.go.textContent = t(ended ? 'games.again' : 'games.startGame'); lob.go.onclick = () => send({ t: startMsg });

      }
    }
  }
  // 设置 → 语言: the full screens' text again (the lobbies redraw on the tick below)
  onLang(() => { if (G.phase !== 'idle') gRender(); if (S.phase !== 'idle') sRender(); });
  setInterval(() => {
    render();
    const gt = G.open && (G.phase === 'drawing' || G.phase === 'reveal') ? String(secsLeft(G.endsAt)) : ''; put(g.time, gt && 'timer', gt);
    g.time.classList.toggle('hurry', G.phase === 'drawing' && secsLeft(G.endsAt) <= 10);
    const st = S.open && ['describe', 'vote', 'reveal', 'over'].includes(S.phase) ? String(secsLeft(S.endsAt)) : ''; put(s.time, st && 'timer', st);
    s.time.classList.toggle('hurry', (S.phase === 'describe' || S.phase === 'vote') && secsLeft(S.endsAt) <= 10);
    if (S.open) voiceNote(sVoice, spyVoice(), micOn()); // (M pressed)
    ww.tick();
    mind.render(); mind.tick();
    ob.render();
  }, 250);

  function msg(m) {
    if (m.t === 'g_state') {
      const fresh = m.phase === 'drawing' && m.round === 1 && (G.phase === 'idle' || G.phase === 'over');
      if (m.drawer !== myId() || m.phase !== 'drawing') { G.word = ''; G.undo = G.redo = 0; paintTools(); }
      Object.assign(G, m);
      if (fresh) { G.dismissed = false; g.log.replaceChildren(); }
      if (m.phase !== 'idle' && !G.dismissed) G.open = true;
      if (m.phase === 'idle') G.open = false;
      gRender(); render();
    } else if (m.t === 'g_word') { G.word = m.word; gRender(); }
    else if (m.t === 'g_draw') stroke(m);
    else if (m.t === 'g_board') gBoard(m.strokes ?? []);
    else if (m.t === 'g_hist') { G.undo = m.undo; G.redo = m.redo; paintTools(); }
    else if (m.t === 'g_clear') gClear();
    else if (m.t === 'g_msg') addLog(g.log, m, 'games.pict.log');
    else if (m.t === 'g_over') { G.scores = m.board; gRender(); }
    else if (m.t === 's_state') {
      const fresh = m.phase === 'describe' && m.game === 1 && m.round === 1 && (S.phase === 'idle' || S.phase === 'final');
      if (m.phase !== 'vote' || S.phase !== 'vote') S.myVote = null;
      if (m.phase === 'idle' || (m.game !== S.game)) S.word = '';
      Object.assign(S, m);
      if (fresh) { S.dismissed = false; s.log.replaceChildren(); }
      if (m.phase !== 'idle' && !S.dismissed) S.open = true;
      if (m.phase === 'idle') S.open = false;
      sRender(); render();
    } else if (m.t === 's_word') { S.word = m.word; sRender(); }
    else if (m.t === 's_msg') { addLog(s.log, m, 'games.spy.log'); if (!m.name && !m.good) S.note = m; }
    else if (m.t === 's_over') { S.scores = m.board; sRender(); }
    else if (m.t.startsWith('w_')) ww.msg(m);
    else if (m.t.startsWith('m_')) mind.msg(m);
    else if (m.t.startsWith('ob_')) ob.msg(m);
  }
  onLang(() => { gRender(); sRender(); }); // (the away marks)
  /** I'm a living player of the spy hut's game while it runs, in the hut (the office's voice.ts sealed()). */
  const sealed = () => {
    if (!inZone('spy')) return false;
    const mine = myId();
    return (ww.running() && !!W.seats.find((x) => x.id === mine)?.alive) || ((S.phase === 'describe' || S.phase === 'vote') && !!S.order.find((x) => x.id === mine)?.alive);
  };
  return { msg, sealed };
}
